import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Network, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Textarea } from '@/components/ui/textarea'
import { formatDateTime } from '@/lib/dateUtils'
import { useCan } from '@/hooks/useCan'
import { useMobilisationTeamsBySet } from '../hooks'
import { useCreateAssessment, useDeleteAssessment, useImpactAssessments, useUpdateAssessment } from '../impact-assessment'
import { ImpactOverview } from '../components/impact/ImpactOverview'
import { ImpactScenarios } from '../components/impact/ImpactScenarios'
import { ImpactToRequest } from '../components/impact/ImpactToRequest'
import { ImpactChanges } from '../components/impact/ImpactChanges'
import { Step4TechnicalChecklist } from '../components/mobilisation/Step4TechnicalChecklist'
import { Step5ImpactAssessment } from '../components/mobilisation/Step5ImpactAssessment'

/**
 * Impact Assessment: its own page on the main menu, after Recovery Scenarios and before
 * Recovery Manager.
 *
 * An impact assessment is its own record (Andrew, 14 Oct 2026: "Start impact assessment should not
 * create a recovery set ... A recovery set should only be created when we know we are going to put
 * recovery requests in the recovery set"; "build the impact assessment as its own record"). It has
 * a heading, a description, who started it and when, and every change made to it since.
 *
 *   By location and platform  what is compromised, the business services impacted, and what that
 *                             does to each recovery scenario.
 *   Every object              the same marks at object level.
 *   What to request           the stacks that bring the impacted services back; each is requested
 *                             on its own, then put in a recovery set - existing or new.
 *   Technical checklist       belongs to a recovery set, so it shows for a set made from here.
 *   Changes                   who changed what, and when.
 *   On the canvas             the same marks drawn on the hierarchy (its own page).
 */
export type AssessmentView = 'summary' | 'objects' | 'request' | 'checklist' | 'changes'
const VIEWS: AssessmentView[] = ['summary', 'objects', 'request', 'checklist', 'changes']

const problemOf = (error: unknown) =>
    (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error
    || (error as { response?: { data?: { message?: string } } })?.response?.data?.message
    || (error as Error)?.message
    || null

export function ImpactAssessmentPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const canManage = useCan('recover-requests.update')
    // Deleting is its own permission: someone who can start and mark an assessment cannot remove one.
    const canDelete = useCan('recover-requests.delete')
    const [params, setParams] = useSearchParams()
    const { data: assessments = [], isLoading } = useImpactAssessments()

    // The assessment in the address, or else the newest.
    const asked = Number.parseInt(params.get('assessment') || '', 10)
    const current = assessments.find((a) => a.id === asked) ?? assessments[0] ?? null
    const assessmentId = current?.id ?? null
    const view: AssessmentView = VIEWS.includes(params.get('view') as AssessmentView) ? (params.get('view') as AssessmentView) : 'summary'
    const go = (next: { assessment?: number; view?: AssessmentView }) =>
        setParams({ assessment: String(next.assessment ?? assessmentId ?? ''), view: next.view ?? view }, { replace: true })

    // The technical checklist belongs to a recovery set: one made from this assessment.
    const linkedSets = current?.recoverySets ?? []
    const [checklistSet, setChecklistSet] = useState<number | null>(null)
    const setId = linkedSets.find((s) => s.id === checklistSet)?.id ?? linkedSets[0]?.id ?? null
    const { data: teams = [] } = useMobilisationTeamsBySet(view === 'checklist' ? setId : null)

    // Wider than the app's usual column: four lists side by side, or two tables.
    useEffect(() => {
        const container = document.querySelector('main > div') as HTMLElement | null
        if (!container) return undefined
        const before = container.style.maxWidth
        container.style.maxWidth = 'none'
        return () => { container.style.maxWidth = before }
    }, [])

    // Start and edit share one form.
    const create = useCreateAssessment()
    const update = useUpdateAssessment(assessmentId)
    const remove = useDeleteAssessment()
    const [editing, setEditing] = useState<'start' | 'edit' | null>(null)
    const [heading, setHeading] = useState('')
    const [description, setDescription] = useState('')
    const [formProblem, setFormProblem] = useState<string | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [deleteProblem, setDeleteProblem] = useState<string | null>(null)

    const openForm = (mode: 'start' | 'edit') => {
        setFormProblem(null)
        setHeading(mode === 'edit' ? current?.heading ?? '' : '')
        setDescription(mode === 'edit' ? current?.description ?? '' : '')
        setEditing(mode)
    }
    const saveForm = () => {
        setFormProblem(null)
        const body = { heading: heading.trim(), description: description.trim() }
        if (editing === 'start') {
            create.mutate(body, {
                onSuccess: (made) => { setEditing(null); if (made?.id != null) setParams({ assessment: String(made.id), view: 'summary' }, { replace: true }) },
                onError: (error) => setFormProblem(problemOf(error)),
            })
        } else {
            update.mutate(body, { onSuccess: () => setEditing(null), onError: (error) => setFormProblem(problemOf(error)) })
        }
    }

    return (
        <div className="space-y-3" data-testid="impact-assessment-page">
            <header className="space-y-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <Button variant="ghost" size="sm" className="w-fit" onClick={() => navigate(-1)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('common.back')}
                </Button>
                <h1 className="text-lg font-semibold">{t('recoverRequests.mobilisation.impactAssessment', 'Impact Assessment')}</h1>
                {assessments.length > 0 && (
                    <Select value={assessmentId == null ? undefined : String(assessmentId)} onValueChange={(value) => go({ assessment: Number(value) })}>
                        <SelectTrigger className="h-8 w-60 text-sm" aria-label={t('recoverRequests.impact.page.assessment', 'Impact assessment')}><SelectValue /></SelectTrigger>
                        <SelectContent>{assessments.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.heading}</SelectItem>)}</SelectContent>
                    </Select>
                )}
                {canManage && (
                    <Button variant="outline" size="sm" onClick={() => openForm('start')} data-testid="impact-start">
                        <Plus className="mr-1.5 h-3.5 w-3.5" />
                        {t('recoverRequests.impact.page.start', 'Start impact assessment')}
                    </Button>
                )}
                {canManage && current && (
                    <Button variant="ghost" size="sm" onClick={() => openForm('edit')} data-testid="impact-edit">
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                        {t('common.edit', 'Edit')}
                    </Button>
                )}
                {canDelete && current && (
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => { setDeleteProblem(null); setDeleting(true) }} data-testid="impact-delete">
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        {t('common.delete', 'Delete')}
                    </Button>
                )}
                {current && (
                    <Button variant="outline" size="sm" className="ml-auto" onClick={() => navigate(`/impact-assessment/${current.id}/canvas`)} data-testid="impact-open-canvas">
                        <Network className="mr-1.5 h-3.5 w-3.5" />
                        {t('recoverRequests.impact.openCanvas', 'Open on the canvas')}
                    </Button>
                )}
                </div>
                {/* The views: their own line, under the heading (Andrew, 16 Oct 2026). */}
                {current && (
                    <div className="flex" data-testid="impact-views">
                        <SegmentedControl<AssessmentView>
                            value={view}
                            onValueChange={(next) => go({ view: next })}
                            ariaLabel={t('recoverRequests.managerGroups.views', 'Views')}
                            options={[
                                { value: 'summary', label: t('recoverRequests.impact.page.summary', 'By location and platform') },
                                { value: 'objects', label: t('recoverRequests.impact.page.objects', 'Every object') },
                                { value: 'request', label: t('recoverRequests.impact.page.request', 'What to request') },
                                { value: 'checklist', label: t('recoverRequests.mobilisation.stepLabel.TechnicalChecklist', 'Technical Checklist') },
                                { value: 'changes', label: t('recoverRequests.impact.page.changes', 'Changes') },
                            ]}
                        />
                    </div>
                )}
            </header>
            {current && (
                <p className="text-xs text-muted-foreground" data-testid="impact-assessment-record">
                    {current.description ? `${current.description} · ` : ''}
                    {t('recoverRequests.impact.page.started', 'Started {{when}} by {{who}}', { when: current.createdAt ? formatDateTime(current.createdAt) : '—', who: current.createdByName || '—' })}
                    {current.updatedAt && current.updatedAt !== current.createdAt && ` · ${t('recoverRequests.impact.page.lastChanged', 'last changed {{when}} by {{who}}', { when: formatDateTime(current.updatedAt), who: current.updatedByName || '—' })}`}
                    {linkedSets.length > 0 && ` · ${t('recoverRequests.impact.page.inSets', 'Recovery sets: {{list}}', { list: linkedSets.map((s) => s.name).join(', ') })}`}
                </p>
            )}

            <Dialog open={deleting} onOpenChange={setDeleting}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('recoverRequests.impact.page.deleteTitle', 'Delete “{{name}}”?', { name: current?.heading ?? '' })}</DialogTitle>
                        <DialogDescription>{t('recoverRequests.impact.page.deleteHelpRecord', 'Everything marked in this impact assessment, any recorded decision and its history are removed with it. This cannot be undone. An assessment whose requests are in a recovery set cannot be deleted.')}</DialogDescription>
                    </DialogHeader>
                    {deleteProblem && <p role="alert" className="text-sm font-medium text-destructive">{deleteProblem}</p>}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleting(false)}>{t('common.cancel', 'Cancel')}</Button>
                        <Button
                            variant="destructive"
                            disabled={remove.isPending || assessmentId == null}
                            onClick={() => remove.mutate(assessmentId as number, {
                                onSuccess: () => { setDeleting(false); setParams({ view: 'summary' }, { replace: true }) },
                                onError: (error) => setDeleteProblem(problemOf(error)),
                            })}
                        >
                            {t('recoverRequests.impact.page.deleteConfirm', 'Delete the impact assessment')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={editing != null} onOpenChange={(open) => { if (!open) setEditing(null) }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editing === 'edit' ? t('recoverRequests.impact.page.editTitle', 'Edit the impact assessment') : t('recoverRequests.impact.page.start', 'Start impact assessment')}</DialogTitle>
                        <DialogDescription>
                            {editing === 'edit'
                                ? t('recoverRequests.impact.page.editHelp', 'The change is recorded with your name and the date and time.')
                                : t('recoverRequests.impact.page.startHelpRecord', 'Recorded with the date and time and your name. It starts with nothing marked compromised. No recovery set is made until a request is put in one.')}
                        </DialogDescription>
                    </DialogHeader>
                    <label className="block text-sm font-medium">
                        {t('recoverRequests.impact.page.heading', 'Heading')}
                        <Input className="mt-1" maxLength={100} value={heading} onChange={(e) => setHeading(e.target.value)} placeholder={t('recoverRequests.impact.page.headingExample', 'For example: Ransomware attack, 14 October')} data-testid="impact-heading" />
                    </label>
                    <label className="block text-sm font-medium">
                        {t('recoverRequests.impact.page.description', 'Description')}
                        <Textarea className="mt-1" rows={4} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} />
                    </label>
                    {formProblem && <p role="alert" className="text-sm font-medium text-destructive">{formProblem}</p>}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>{t('common.cancel', 'Cancel')}</Button>
                        <Button onClick={saveForm} disabled={!heading.trim() || create.isPending || update.isPending} data-testid="impact-save">
                            {editing === 'edit' ? t('common.save', 'Save') : t('recoverRequests.impact.page.startConfirm', 'Start')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
                : assessmentId == null ? <p className="text-sm text-muted-foreground" data-testid="impact-no-assessment">{t('recoverRequests.impact.page.noAssessment', 'No impact assessment has been started yet.')}</p>
                    : view === 'summary' ? (
                        <>
                            <ImpactOverview key={assessmentId} assessmentId={assessmentId} />
                            <ImpactScenarios key={`scenarios-${assessmentId}`} assessmentId={assessmentId} />
                        </>
                    ) : view === 'request' ? <ImpactToRequest key={assessmentId} assessmentId={assessmentId} canRequest={canManage} />
                        : view === 'changes' ? <ImpactChanges key={assessmentId} assessmentId={assessmentId} />
                            : view === 'checklist' && setId == null ? (
                                <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground" data-testid="impact-checklist-no-set">
                                    {t('recoverRequests.impact.page.checklistNoSet', 'The technical checklist belongs to a recovery set. It shows here once a request from this assessment is in a recovery set.')}
                                </p>
                            ) : (
                                // Without recover-requests.update everything is shown with its controls disabled.
                                <fieldset disabled={!canManage} className="m-0 min-w-0 space-y-3 rounded-lg border bg-card p-4">
                                    {view === 'checklist' ? (
                                        <>
                                            {linkedSets.length > 1 && (
                                                <Select value={String(setId)} onValueChange={(v) => setChecklistSet(Number(v))}>
                                                    <SelectTrigger className="h-8 w-60 text-sm" aria-label={t('recoverRequests.impact.page.checklistSet', 'Recovery set')}><SelectValue /></SelectTrigger>
                                                    <SelectContent>{linkedSets.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}</SelectContent>
                                                </Select>
                                            )}
                                            <Step4TechnicalChecklist teams={teams} setId={setId} onOpenImpactAssessment={() => go({ view: 'summary' })} />
                                        </>
                                    ) : <Step5ImpactAssessment key={assessmentId} assessmentId={assessmentId} hideHeading />}
                                </fieldset>
                            )}
        </div>
    )
}
